import { i18n } from "@libs/i18n";
import { EnrolmentInvoiceSummaryType } from "@/domain/enrolment";
import { InvoiceStatusType } from "@/domain/invoice";
import { dateAsDayjs } from "@/utils/date";

export const formatInvoiceStatus = (status: InvoiceStatusType): string => {
  switch (status) {
    case "PAID":
      return i18n.t("main:enrolments.details.invoice.status.paid");
    case "DUE":
      return i18n.t("main:enrolments.details.invoice.status.due");
    case "OVERDUE":
      return i18n.t("main:enrolments.details.invoice.status.overdue");
    case "DRAFT":
      return i18n.t("main:enrolments.details.invoice.status.draft");
    case "CANCELLED":
      return i18n.t("main:enrolments.details.invoice.status.cancelled");
    case "PARTIAL":
      return i18n.t("main:enrolments.details.invoice.status.partial");
    default:
      return status;
  }
};

// Money is stored as double precision, so balance comparisons use a small
// tolerance rather than exact equality.
const MONEY_TOLERANCE = 0.005;

type InvoiceForTotalsType = {
  status: InvoiceStatusType;
  is_placeholder: boolean;
  total: number;
  invoice_items: { amount: number }[];
};

const computeInvoiceItemsTotal = (
  invoice: Pick<InvoiceForTotalsType, "invoice_items">,
): number => invoice.invoice_items.reduce((sum, item) => sum + item.amount, 0);

// Total shown in the Invoices summary: placeholders contribute their tracked
// total, real invoices contribute the sum of their line items. Cancelled
// invoices are excluded from totals/aggregates (per spec); their cards still
// render so they can be restored.
export const computeTotalInvoices = (
  invoices: InvoiceForTotalsType[],
): number =>
  invoices
    .filter(invoice => invoice.status !== "CANCELLED")
    .reduce(
      (sum, invoice) =>
        sum +
        (invoice.is_placeholder
          ? invoice.total
          : computeInvoiceItemsTotal(invoice)),
      0,
    );

// The real (non-placeholder, non-cancelled) invoices — the ones that must
// balance the course cost before a contract can be signed. Placeholders are
// excluded and may legitimately imbalance the summary.
const getRealInvoices = (
  invoices: InvoiceForTotalsType[],
): InvoiceForTotalsType[] =>
  invoices.filter(
    invoice => !invoice.is_placeholder && invoice.status !== "CANCELLED",
  );

// Sum of only the real (non-placeholder, non-cancelled) invoices.
const computeRealInvoicesTotal = (invoices: InvoiceForTotalsType[]): number =>
  getRealInvoices(invoices).reduce(
    (sum, invoice) => sum + computeInvoiceItemsTotal(invoice),
    0,
  );

// Gate for the Sign action. The balance rule only applies once at least one
// real invoice exists; enrolments with zero real invoices (legacy /
// placeholder-only / not-yet-invoiced) are allowed to sign without it.
export const realInvoicesBalanceCourseCost = (
  invoices: InvoiceForTotalsType[],
  totalCourseCost: number,
): boolean => {
  if (getRealInvoices(invoices).length === 0) return true;
  return (
    Math.abs(computeRealInvoicesTotal(invoices) - totalCourseCost) <=
    MONEY_TOLERANCE
  );
};

type InvoiceForAggregationType = {
  status: InvoiceStatusType;
  is_placeholder: boolean;
};

const INVOICE_STATUS_PRECEDENCE: InvoiceStatusType[] = [
  "OVERDUE",
  "DRAFT",
  "DUE",
  "PARTIAL",
  "PAID",
];

export const aggregateInvoiceStatus = (
  invoices: InvoiceForAggregationType[],
): InvoiceStatusType | null => {
  const real = invoices.filter(
    invoice => !invoice.is_placeholder && invoice.status !== "CANCELLED",
  );
  if (real.length === 0) return null;
  for (const status of INVOICE_STATUS_PRECEDENCE) {
    if (real.some(invoice => invoice.status === status)) return status;
  }
  return null;
};

export type OnScheduleStatusType =
  | { kind: "paid_in_full" }
  | { kind: "on_schedule" }
  | { kind: "ahead"; amount: number }
  | { kind: "behind"; amount: number };

export const computeOnScheduleStatus = ({
  invoiceTotal,
  paidEntries,
  scheduleEntries,
}: {
  invoiceTotal: number;
  paidEntries: { amount: number; date: string }[];
  scheduleEntries: { total: number; due_date: string }[];
}): OnScheduleStatusType => {
  const today = dateAsDayjs().utc().startOf("day");
  // Payments dated today still count as "received by today" (mid-day
  // timestamps shouldn't be excluded).
  const onOrBefore = (iso: string) =>
    !dateAsDayjs(iso).utc().startOf("day").isAfter(today);
  // The "behind" cutoff is strictly before today: an installment due today is
  // not yet overdue. Mirrors the SQL recompute (`due_date < today`) so the
  // client-derived status doesn't flip a day early.
  const beforeToday = (iso: string) =>
    dateAsDayjs(iso).utc().startOf("day").isBefore(today);

  // Two different schedule totals are needed:
  //  - scheduledByYesterday (strict <) gates the "behind" decision so paying
  //    on the due date itself isn't treated as late;
  //  - scheduledByToday (inclusive) gates the "ahead" decision so paying
  //    today's installment is "on schedule", not "ahead".
  const scheduledByYesterday = scheduleEntries
    .filter(s => beforeToday(s.due_date))
    .reduce((sum, s) => sum + s.total, 0);
  const scheduledByToday = scheduleEntries
    .filter(s => onOrBefore(s.due_date))
    .reduce((sum, s) => sum + s.total, 0);

  const paidByToday = paidEntries
    .filter(p => onOrBefore(p.date))
    .reduce((sum, p) => sum + p.amount, 0);

  if (paidByToday >= invoiceTotal) return { kind: "paid_in_full" };
  if (paidByToday < scheduledByYesterday)
    return { kind: "behind", amount: scheduledByYesterday - paidByToday };
  if (paidByToday > scheduledByToday)
    return { kind: "ahead", amount: paidByToday - scheduledByToday };
  return { kind: "on_schedule" };
};

export type EnrolmentPaymentStatusType =
  | "OVERDUE"
  | "PAID"
  | "ON_TRACK"
  | "OUTSTANDING";

type InvoiceForStatusType = {
  id: string;
  status: InvoiceStatusType;
  is_placeholder: boolean;
  total: number;
  payment_schedule: { total: number; due_date: string }[];
};

type PaymentForStatusType = {
  invoice_id: string | null;
  amount: number;
  date_paid: string | null;
  due_date: string;
};

const onScheduleStatusForInvoice = (
  invoice: InvoiceForStatusType,
  payments: PaymentForStatusType[],
): OnScheduleStatusType => {
  const invoicePayments = payments.filter(
    payment => payment.invoice_id === invoice.id,
  );
  return computeOnScheduleStatus({
    invoiceTotal: invoice.total ?? 0,
    paidEntries: invoicePayments.map(payment => ({
      amount: payment.amount,
      date: payment.date_paid ?? payment.due_date,
    })),
    scheduleEntries: invoice.payment_schedule.map(entry => ({
      total: entry.total,
      due_date: entry.due_date,
    })),
  });
};

// Rolls an enrolment's invoices up into a single payment status. OVERDUE wins
// (any invoice behind its schedule); PAID only when every active invoice is paid
// in full; otherwise ON_TRACK. Legacy enrolments with no real invoices surface
// as OUTSTANDING when a product balance is still owing, or PAID once the
// product total has been fully covered. PENDING enrolments suppress the badge
// entirely — payment status is not meaningful until the enrolment is active.
export const computeEnrolmentPaymentStatus = ({
  invoices,
  payments,
  productsTotal,
  enrolmentStatus,
}: {
  invoices: InvoiceForStatusType[];
  payments: PaymentForStatusType[];
  productsTotal: number;
  enrolmentStatus?: string | null;
}): EnrolmentPaymentStatusType | null => {
  if (enrolmentStatus === "PENDING") return null;

  const active = invoices.filter(
    invoice =>
      !invoice.is_placeholder &&
      invoice.status !== "CANCELLED" &&
      invoice.status !== "DRAFT",
  );

  if (active.length > 0) {
    const statuses = active.map(invoice =>
      onScheduleStatusForInvoice(invoice, payments),
    );
    if (statuses.some(status => status.kind === "behind")) return "OVERDUE";
    if (statuses.every(status => status.kind === "paid_in_full")) return "PAID";
    return "ON_TRACK";
  }

  const hasRealInvoices = invoices.some(
    invoice => !invoice.is_placeholder && invoice.status !== "CANCELLED",
  );
  if (!hasRealInvoices) {
    const paid = payments.reduce(
      (sum, payment) => sum + (payment.amount ?? 0),
      0,
    );
    if (productsTotal - paid > 0.005) return "OUTSTANDING";
    return "PAID";
  }

  return null;
};

export type InvoiceForEnrolmentSummaryType = InvoiceForStatusType & {
  created_at: string;
  invoice_items: { amount: number }[];
  payments: {
    amount: number;
    status: string;
    date_paid: string | null;
    due_date: string;
  }[];
};

export const computeEnrolmentInvoiceSummary = ({
  invoices,
  productsTotal,
  enrolmentStatus,
}: {
  invoices: InvoiceForEnrolmentSummaryType[];
  productsTotal: number;
  enrolmentStatus?: string | null;
}): EnrolmentInvoiceSummaryType => {
  const sortedInvoices = [...invoices].sort((a, b) => {
    const valueA = a.created_at;
    const valueB = b.created_at;
    return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
  });

  const realInvoices = sortedInvoices.filter(
    invoice => !invoice.is_placeholder && invoice.status !== "CANCELLED",
  );

  // Only PAID payments count toward the schedule. Payments are soft-deleted to
  // CANCELLED (and default to DRAFT), so unfiltered rows would inflate the
  // amount paid and mask an overdue invoice. This matches the outstanding calc
  // below and the details view, which both consider PAID payments only.
  const allPayments = sortedInvoices.flatMap(invoice =>
    invoice.payments
      .filter(payment => payment.status === "PAID")
      .map(payment => ({
        invoice_id: invoice.id,
        amount: payment.amount,
        date_paid: payment.date_paid,
        due_date: payment.due_date,
      })),
  );

  const invoice_status = aggregateInvoiceStatus(sortedInvoices);
  const invoice_count = realInvoices.length;
  const placeholder = sortedInvoices.find(invoice => invoice.is_placeholder);
  const placeholder_status = placeholder?.status ?? null;
  const payment_status = computeEnrolmentPaymentStatus({
    invoices: sortedInvoices,
    payments: allPayments,
    productsTotal,
    enrolmentStatus,
  });

  // Outstanding is always the enrolment's product (course) cost minus every
  // PAID payment recorded against it, regardless of how the enrolment is
  // invoiced. It deliberately does NOT read invoice line items or invoice
  // totals: real invoices in production routinely carry a `total` (or nothing)
  // with no `invoice_items` rows, so the previous per-invoice calc treated the
  // owed side as 0 and rendered a negative balance the moment any payment
  // landed. `productsTotal` (sum of enrolments_products.price) is the reliable
  // owed figure. A genuine overpayment stays negative here by design; the paid
  // badge is derived independently by computeEnrolmentPaymentStatus.
  const totalPaid = allPayments.reduce(
    (sum, payment) => sum + (payment.amount ?? 0),
    0,
  );
  const outstanding = productsTotal - totalPaid;

  return {
    outstanding,
    invoice_status,
    invoice_count,
    payment_status,
    placeholder_status,
  };
};

export const getPaymentStatusBadgeVariant = (
  status: EnrolmentPaymentStatusType,
): "destructive" | "default" | "warning" | "secondary" => {
  switch (status) {
    case "OVERDUE":
      return "destructive";
    case "PAID":
      return "default";
    case "OUTSTANDING":
      return "warning";
    default:
      return "secondary";
  }
};

export type EnrolmentOverdueDetailsType = {
  amount: number;
  oldestDueDate: string;
  daysOverdue: number;
};

// How much an enrolment is behind its payment schedule and since when. Returns
// null unless at least one active invoice is behind (i.e. status is OVERDUE).
export const computeEnrolmentOverdueDetails = (
  invoices: InvoiceForStatusType[],
  payments: PaymentForStatusType[],
): EnrolmentOverdueDetailsType | null => {
  const today = dateAsDayjs().utc().startOf("day");
  const onOrBefore = (iso: string) =>
    !dateAsDayjs(iso).utc().startOf("day").isAfter(today);
  // An installment due today isn't overdue yet (matches
  // computeOnScheduleStatus and the SQL recompute rule).
  const beforeToday = (iso: string) =>
    dateAsDayjs(iso).utc().startOf("day").isBefore(today);

  let amount = 0;
  let oldest: ReturnType<typeof dateAsDayjs> | null = null;

  const active = invoices.filter(
    invoice =>
      !invoice.is_placeholder &&
      invoice.status !== "CANCELLED" &&
      invoice.status !== "DRAFT",
  );

  for (const invoice of active) {
    const status = onScheduleStatusForInvoice(invoice, payments);
    if (status.kind !== "behind") continue;

    const invoicePayments = payments.filter(
      payment => payment.invoice_id === invoice.id,
    );

    amount += status.amount;

    // Allocate payments to scheduled installments oldest-first; the first
    // installment they can't cover marks when this invoice fell behind.
    const paidByToday = invoicePayments
      .filter(payment => onOrBefore(payment.date_paid ?? payment.due_date))
      .reduce((sum, payment) => sum + payment.amount, 0);
    const dueInstallments = invoice.payment_schedule
      .filter(entry => beforeToday(entry.due_date))
      .sort((a, b) =>
        dateAsDayjs(a.due_date).isAfter(dateAsDayjs(b.due_date)) ? 1 : -1,
      );

    let remaining = paidByToday;
    for (const installment of dueInstallments) {
      if (remaining >= installment.total) {
        remaining -= installment.total;
        continue;
      }
      const dueDay = dateAsDayjs(installment.due_date).utc().startOf("day");
      if (!oldest || dueDay.isBefore(oldest)) oldest = dueDay;
      break;
    }
  }

  if (amount <= 0 || !oldest) return null;

  return {
    amount,
    oldestDueDate: oldest.toISOString(),
    daysOverdue: today.diff(oldest, "day"),
  };
};
